import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import fleetApi from "@/lib/fleetApi";
import { wideTrips } from "@/lib/tripsFetch";
import { todayKey } from "@/lib/time";
import { useAuth } from "@/context/AuthContext";
import { downloadCSV, downloadReportPDF } from "@/lib/exporters";
import { Panel, PageHeader, Loading, KpiCard } from "@/components/portal/widgets";
import { Button } from "@/components/ui/button";
import { BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { Route, ShieldAlert, Star, FileText, Sheet, Loader2, FileWarning, DollarSign } from "lucide-react";

const COLORS = ["#2563eb", "#22C55E", "#F97316", "#EF4444", "#14B8A6", "#6b7280", "#8b5cf6"];
const countBy = (arr, key) => arr.reduce((m, x) => { const k = x[key] || "unknown"; m[k] = (m[k] || 0) + 1; return m; }, {});
const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

const Money = ({ label, value, sub }) => (
  <div className="rounded-lg border border-slate-800 bg-[#0f1623] p-4" data-testid={`income-${label.toLowerCase().replace(/\s+/g, "-")}`}>
    <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{label}</div>
    <div className="mt-1 font-mono-data text-2xl font-bold text-white">{money(value)}</div>
    {sub && <div className="mt-0.5 text-xs text-slate-500">{sub}</div>}
  </div>
);

export default function ReportsPage() {
  const { organization, user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    Promise.all([wideTrips(user?.role === "owner").then((data) => ({ data })).catch(() => ({ data: [] })), fleetApi.get("/incidents"), fleetApi.get("/drivers"), fleetApi.get("/compliance"), fleetApi.get("/income/daily").catch(() => ({ data: null }))])
      .then(([t, i, d, c, inc]) => setData({ trips: t.data, incidents: i.data, drivers: d.data, compliance: c.data?.records || [], counts: c.data?.counts || {}, income: inc.data }))
      .catch(() => {});
  }, [user?.role]);

  if (!data) return <Loading />;

  const byStatus = Object.entries(countBy(data.trips, "status")).map(([name, value]) => ({ name, value }));
  const byRisk = Object.entries(countBy(data.trips, "risk")).map(([name, value]) => ({ name: name.replace(/_/g, " "), value }));
  const byType = Object.entries(countBy(data.incidents, "type")).map(([name, value]) => ({ name, value }));
  const compByStatus = countBy(data.compliance, "status");
  const topDrivers = [...data.drivers].sort((a, b) => (b.metrics?.on_time_rate || 0) - (a.metrics?.on_time_rate || 0)).slice(0, 8);

  const org = organization?.name || "RideStamp";
  const stamp = todayKey();

  const buildSections = () => [
    { title: "Trips by Status", headers: ["Status", "Count"], rows: byStatus.map((s) => [s.name, s.value]) },
    { title: "Trips by Risk", headers: ["Risk", "Count"], rows: byRisk.map((s) => [s.name, s.value]) },
    { title: "Incidents by Type", headers: ["Type", "Count"], rows: byType.map((s) => [s.name, s.value]) },
    { title: "Compliance by Status", headers: ["Status", "Count"], rows: Object.entries(compByStatus).map(([k, v]) => [k, v]) },
    { title: "Top Drivers (On-Time)", headers: ["Driver", "On-Time %", "Trips", "Incidents"], rows: topDrivers.map((d) => [d.full_name || d.name, d.metrics?.on_time_rate ?? "", d.metrics?.trips_completed ?? "", d.metrics?.incidents ?? ""]) },
  ];

  const run = async (kind) => {
    setExporting(true);
    try {
      const s = buildSections();
      if (kind === "pdf") downloadReportPDF(`RideStamp-Report-${stamp}.pdf`, "Operations Report", org, s);
      else downloadCSV(`RideStamp-Report-${stamp}.csv`, s);
      toast.success(`${kind.toUpperCase()} downloaded.`);
    } catch (e) { toast.error("Export failed."); } finally { setExporting(false); }
  };

  return (
    <div data-testid="reports-page">
      <PageHeader title="Reports & Analytics" subtitle="Live operational performance and accountability metrics.">
        <div className="flex gap-2">
          <Button onClick={() => run("csv")} disabled={exporting} data-testid="export-csv-btn" variant="outline"
            className="gap-2 rounded-lg border-slate-700 bg-slate-900 font-semibold text-slate-200 hover:bg-slate-800 hover:text-white">
            {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sheet className="h-4 w-4" />} CSV
          </Button>
          <Button onClick={() => run("pdf")} disabled={exporting} data-testid="export-pdf-btn"
            className="gap-2 rounded-lg bg-blue-600 font-semibold text-white hover:bg-blue-500">
            {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />} PDF
          </Button>
        </div>
      </PageHeader>

      <div className="mb-6 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <KpiCard icon={Route} label="Total Trips" value={data.trips.length} accent="blue" testid="report-total-trips" to="/portal/dispatch" />
        <KpiCard icon={ShieldAlert} label="Incidents" value={data.incidents.length} accent="red" testid="report-incidents" to="/portal/incidents" />
        <KpiCard icon={FileWarning} label="Compliance Records" value={data.compliance.length} accent="amber" testid="report-compliance" to="/portal/incidents?tab=compliance" />
        <KpiCard icon={Star} label="Top On-Time" value={`${topDrivers[0]?.metrics?.on_time_rate ?? 0}%`} sub={topDrivers[0]?.full_name} accent="emerald" testid="report-top-driver" to="/portal/fleet" />
      </div>

      {data.income && (
        <Panel className="mb-6" title="Revenue · Owner Income" testid="panel-income"
          action={<span className={`font-mono-data text-xs font-bold ${(data.income.month_change_pct ?? 0) >= 0 ? "text-emerald-400" : "text-red-400"}`} data-testid="income-mom">{(data.income.month_change_pct ?? 0) >= 0 ? "+" : ""}{data.income.month_change_pct ?? 0}% MoM</span>}>
          <div className="grid gap-4 p-5 sm:grid-cols-4">
            <Money label="Today" value={data.income.today} sub={`${data.income.today_trips ?? 0} trips`} />
            <Money label="This Week" value={data.income.week_total} />
            <Money label="This Month" value={data.income.month_total} />
            <Money label="Last Month" value={data.income.last_month_total} />
          </div>
          {Array.isArray(data.income.days) && data.income.days.length > 0 && (
            <div className="px-5 pb-5" style={{ height: 220 }} data-testid="income-chart">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data.income.days}><CartesianGrid strokeDasharray="3 3" stroke="#1f2937" />
                  <XAxis dataKey="label" tick={{ fill: "#94a3b8", fontSize: 11 }} /><YAxis tick={{ fill: "#94a3b8", fontSize: 11 }} tickFormatter={(v) => `$${v}`} />
                  <Tooltip contentStyle={{ background: "#111827", border: "1px solid #1f2937", borderRadius: 8, color: "#fff" }} formatter={(v) => [money(v), "income"]} />
                  <Bar dataKey="income" fill="#22C55E" radius={[6, 6, 0, 0]} /></BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </Panel>
      )}

      <div className="grid gap-6 lg:grid-cols-2">
        <Panel title="Trips by Status" testid="chart-trips-status">
          <div className="p-5" style={{ height: 300 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={byStatus}><CartesianGrid strokeDasharray="3 3" stroke="#1f2937" />
                <XAxis dataKey="name" tick={{ fill: "#94a3b8", fontSize: 11 }} /><YAxis tick={{ fill: "#94a3b8", fontSize: 12 }} allowDecimals={false} />
                <Tooltip contentStyle={{ background: "#111827", border: "1px solid #1f2937", borderRadius: 8, color: "#fff" }} />
                <Bar dataKey="value" fill="#2563eb" radius={[6, 6, 0, 0]} /></BarChart>
            </ResponsiveContainer>
          </div>
        </Panel>
        <Panel title="Trips by Risk" testid="chart-trips-risk">
          <div className="p-5" style={{ height: 300 }}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart><Pie data={byRisk} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={95} label={{ fill: "#cbd5e1", fontSize: 11 }}>
                {byRisk.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}</Pie>
                <Tooltip contentStyle={{ background: "#111827", border: "1px solid #1f2937", borderRadius: 8, color: "#fff" }} /></PieChart>
            </ResponsiveContainer>
          </div>
        </Panel>
        <Panel title="Incidents by Type" testid="chart-incidents">
          <div className="p-5" style={{ height: 300 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={byType} layout="vertical"><CartesianGrid strokeDasharray="3 3" stroke="#1f2937" />
                <XAxis type="number" tick={{ fill: "#94a3b8", fontSize: 12 }} allowDecimals={false} /><YAxis type="category" dataKey="name" tick={{ fill: "#94a3b8", fontSize: 10 }} width={110} />
                <Tooltip contentStyle={{ background: "#111827", border: "1px solid #1f2937", borderRadius: 8, color: "#fff" }} />
                <Bar dataKey="value" fill="#F97316" radius={[0, 6, 6, 0]} /></BarChart>
            </ResponsiveContainer>
          </div>
        </Panel>
        <Panel title="Top Drivers · On-Time" testid="driver-performance">
          <div className="divide-y divide-slate-800">
            {topDrivers.map((d, i) => (
              <button key={d.id} type="button" onClick={() => navigate(`/portal/drivers/${d.id}`)}
                data-testid={`report-driver-${d.id}`}
                className="flex w-full items-center justify-between px-5 py-3 text-left transition-colors hover:bg-slate-800/40">
                <div className="flex items-center gap-3"><span className="font-mono-data text-sm text-slate-600">#{i + 1}</span><span className="font-semibold text-white">{d.full_name || d.name}</span></div>
                <div className="flex items-center gap-5 text-sm"><span className="flex items-center gap-1 text-emerald-400"><Star className="h-3.5 w-3.5 fill-emerald-400" />{d.metrics?.on_time_rate ?? "—"}%</span><span className="font-mono-data text-slate-400">{d.metrics?.trips_completed ?? 0} trips</span></div>
              </button>
            ))}
          </div>
        </Panel>
      </div>
    </div>
  );
}
